/* eslint-disable jira/import-whitelist */
import { fetchQuery, graphql } from 'react-relay';
import { checkLicense } from '@jira/spa-apps__interceptors/src/checkLicense.tsx';

import { JIRA_PROJECT_TYPE_SERVICE_DESK_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';

import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { SERVICE_REQUEST_FEATURE } from '@jira/servicedesk__common/src/Enabled.tsx';
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import {
	createResource,
	type RouterDataContext,
	type ResourceStoreContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';

import { performGetRequest } from '@atlassian/jira-fetch';
import { JiraProjectAri } from '@atlassian/ari/jira/project';

import { metrics } from '@atlassian/browser-metrics/factory';

import {
	RESOURCE_TYPE_RESOLUTION_PLAN_SSR,
	RESOLUTION_PLAN_SSR_BUDGET_MS,
} from './resourceTypeContextPanelIssueDetails.constants.tsx';
import { getIssueDetails } from './getIssueDetails.tsx';

import type {
	ResolutionPlanSSRResult,
	ResolutionPlanInitialState,
} from './ResolutionPlanSSRTypes.tsx';
import type { ResolutionPlanSSRResourceQuery } from './__generated__/ResolutionPlanSSRResourceQuery.graphql.ts';
import type { ResolutionPlanSSRResourcePreviewModeQuery } from './__generated__/ResolutionPlanSSRResourcePreviewModeQuery.graphql.ts';

const NOT_ELIGIBLE: ResolutionPlanSSRResult = {
	eligible: false,
	projectId: undefined,
	issueId: undefined,
};

function hasPlanExtras(
	plan: unknown,
): plan is { steps?: unknown; runbooks?: unknown; contentSources?: unknown } {
	return !!plan; // Runtime check is trivial, we trust the query shape
}

type SettledResult<T> = { status: 'fulfilled'; value: T } | { status: 'pending' };

function settle<T>(p: Promise<T>): Promise<SettledResult<T>> {
	return p.then(
		(value) => ({ status: 'fulfilled' as const, value }),
		() => ({ status: 'pending' as const }),
	);
}

// Attaches a passive listener to p at creation time so its settled value can be
// read synchronously after a microtask flush — avoids the race between settle()'s
// async .then() and Promise.resolve({pending}) in the deadline snapshot path.
function createSettler<T>(p: Promise<T>): () => SettledResult<T> {
	let result: SettledResult<T> = { status: 'pending' };
	p.then(
		(value) => {
			result = { status: 'fulfilled', value };
		},
		() => {
			// remain pending on rejection
		},
	);
	return () => result;
}

async function raceToDeadline3<T1, T2, T3>(
	deadline: Promise<unknown>,
	p1: Promise<T1>,
	p2: Promise<T2>,
	p3: Promise<T3>,
): Promise<[SettledResult<T1>, SettledResult<T2>, SettledResult<T3>]> {
	// Attach settlers eagerly so their callbacks are queued as soon as p1/p2 resolve
	const snap1 = createSettler(p1);
	const snap2 = createSettler(p2);
	const snap3 = createSettler(p3);

	return Promise.race([
		// Happy path: both settle before the deadline
		Promise.all([settle(p1), settle(p2), settle(p3)]),
		// Deadline path: read synchronously after one microtask flush
		deadline.then(async () => {
			// One tick lets any already-resolved promise callbacks run
			await Promise.resolve();
			return [snap1(), snap2(), snap3()] satisfies [
				SettledResult<T1>,
				SettledResult<T2>,
				SettledResult<T3>,
			];
		}),
	]);
}

export const ResolutionPlanSSRResource: RouteResource<ResolutionPlanSSRResult | null> =
	createResource<ResolutionPlanSSRResult | null>({
		type: RESOURCE_TYPE_RESOLUTION_PLAN_SSR,
		getKey: ({ match, query }) => {
			const issueKey = match.params.issueKey || query.selectedIssue || '';
			return `ResolutionPlanSSRResource-${issueKey}`;
		},
		getData: async (
			routerDataContext: RouterDataContext,
			resourceStoreContext: ResourceStoreContext,
		) => {
			const resolutionPlanSSRMetric = metrics.custom({
				key: 'resolution-plan-ssr-resource',
			});
			resolutionPlanSSRMetric.start();

			let succeeded = false;
			let deadlineTimerId: ReturnType<typeof setTimeout> | undefined;
			try {
				if (!fg('resolution-plan-panel-ssr')) {
					return null;
				}

				// Budget deadline
				const deadlinePromise = new Promise<'deadline'>((resolve) => {
					deadlineTimerId = setTimeout(() => resolve('deadline'), RESOLUTION_PLAN_SSR_BUDGET_MS);
				});

				const { issueKey } = routerDataContext.match.params;
				const { cloudId, appEditions } = resourceStoreContext.tenantContext;
				const isStandardEdition = appEditions.serviceDesk === 'STANDARD_EDITION';
				const isPremiumEdition = appEditions.serviceDesk === 'PREMIUM_EDITION';

				// ── Cheap sync guards ──
				if (!issueKey) return null;
				if (!checkLicense(resourceStoreContext.tenantContext, 'servicedesk')) return null;

				const shouldShowPanel = isStandardEdition || isPremiumEdition;
				if (!shouldShowPanel) return NOT_ELIGIBLE;

				// ── Phase 1: getIssueDetails (must complete before anything else) ──
				const resolutionPlanIssueDetailsMetric = metrics.custom({
					key: 'resolution-plan-ssr-issue-details',
				});
				resolutionPlanIssueDetailsMetric.start();
				const issueDetails = await getIssueDetails(issueKey, cloudId, true);
				resolutionPlanIssueDetailsMetric.stop();

				if (!issueDetails || !issueDetails.issueId) return NOT_ELIGIBLE;

				const { isAIEnabled, practice, projectType, issueId, projectId } = issueDetails;

				if (projectType !== JIRA_PROJECT_TYPE_SERVICE_DESK_PROJECT) return NOT_ELIGIBLE;
				if (!isAIEnabled) return NOT_ELIGIBLE;

				const isServiceRequest = practice === SERVICE_REQUEST_FEATURE;
				const isNullPracticeEligible =
					practice === undefined && fg('enable-resolution-panel-for-null-field-workitem');
				if (!isServiceRequest && !isNullPracticeEligible) return NOT_ELIGIBLE;
				if (!fg('enable-esa-jsm-ga')) return NOT_ELIGIBLE;

				if (!issueId || !projectId) return NOT_ELIGIBLE;

				const projectAri = JiraProjectAri.create({
					siteId: cloudId,
					projectId,
				}).toString();

				// ── Phase 2: Fire ESA + Preview mode + Plan in parallel, race against remaining budget ──

				// ESA check
				const esaPromise: Promise<{ resolved: boolean; validated: boolean }> = fg(
					'show-resolution-panel-when-request-type-validated',
				)
					? performGetRequest<{ value: { validated?: boolean } }>(
							`/rest/api/3/issue/${issueId}/properties/esaRequestTypeValidated`,
						)
							.then((response) => ({
								resolved: true,
								validated: response?.value?.validated === true,
							}))
							.catch(() => ({ resolved: true, validated: false }))
					: Promise.resolve({ resolved: true, validated: true }); // gate OFF = skip

				// Preview mode check — gated by fg('preview_mode_enabled'). When the
				// gate is OFF we resolve to a "not-resolved + disabled" answer so the
				// downstream visibility helper short-circuits this check entirely and
				// no extra network traffic is generated on rollback.
				const previewModePromise: Promise<{ resolved: boolean; enabled: boolean }> = fg(
					'preview_mode_enabled',
				)
					? fetchQuery<ResolutionPlanSSRResourcePreviewModeQuery>(
							getRelayEnvironment(),
							graphql`
								query ResolutionPlanSSRResourcePreviewModeQuery(
									$jiraProjectAri: ID!
									$experience: JsmChannelsExperience!
								) {
									jsmChannels_isPreviewModeEnabled(
										experience: $experience
										jiraProjectAri: $jiraProjectAri
									) {
										__typename
										... on JsmChannelsPreviewModeConfiguration {
											enabled
										}
									}
								}
							`,
							{ jiraProjectAri: projectAri, experience: 'EMPLOYEE_SERVICE_AGENT' },
							{ fetchPolicy: 'network-only' },
						)
							.toPromise()
							.then((result) => {
								const node = result?.jsmChannels_isPreviewModeEnabled;
								if (node && node.__typename === 'JsmChannelsPreviewModeConfiguration') {
									return { resolved: true, enabled: node.enabled === true };
								}
								return { resolved: false, enabled: false };
							})
							.catch(() => ({ resolved: false, enabled: false }))
					: Promise.resolve({ resolved: false, enabled: false }); // gate OFF = skip

				// Plan query
				const planPromise: Promise<{
					resolved: boolean;
					timedOut: boolean;
					state: ResolutionPlanInitialState | null;
				}> = fetchQuery<ResolutionPlanSSRResourceQuery>(
					getRelayEnvironment(),
					graphql`
						query ResolutionPlanSSRResourceQuery($jiraProjectAri: ID!, $ticketId: ID!) {
							jsmChannels_getServiceAgentResolutionStateByTicketId(
								jiraProjectAri: $jiraProjectAri
								ticketId: $ticketId
							) {
								__typename
								... on JsmChannelsTicketServiceAgentResolutionState {
									conversationId
									isLoading
									message
									plan {
										customPlanNodeAttributes {
											key
											value
										}
										planId
										status
										steps {
											title
											description
											status
											assigneeType
										}
										runbooks {
											confidenceScore
											title
											url
										}
										contentSources {
											ari
											confidenceScore
											title
											url
											type
											product
										}
									}
								}
								... on QueryError {
									message
								}
							}
						}
					`,
					{ jiraProjectAri: projectAri, ticketId: issueId },
					{ fetchPolicy: 'network-only' },
				)
					.toPromise()
					.then((result) => {
						const node = result?.jsmChannels_getServiceAgentResolutionStateByTicketId;

						if (node && node.__typename === 'JsmChannelsTicketServiceAgentResolutionState') {
							const plan = node.plan;

							// Check if plan exists AND satisfy TS that it has these fields via the predicate
							// Note: We combine the checks to handle null/undefined plan
							let planWithExtras = null;
							if (plan && hasPlanExtras(plan)) {
								planWithExtras = plan;
							}
							return {
								resolved: true,
								timedOut: false,
								state: {
									conversationId: node.conversationId ?? null,
									isLoading: node.isLoading ?? null,
									message: node.message ?? null,
									plan: planWithExtras
										? {
												customPlanNodeAttributes: planWithExtras.customPlanNodeAttributes
													? planWithExtras.customPlanNodeAttributes.map((a) => ({
															key: a?.key ?? null,
															value: a?.value ?? null,
														}))
													: null,
												planId: planWithExtras.planId ?? null,
												status: planWithExtras.status ?? null,
												steps: Array.isArray(planWithExtras.steps)
													? planWithExtras.steps.map((step) => {
															// Explicitly read fields to satisfy linter
															// Using optional chaining because generated types don't exist yet (handled by the cast we added earlier)
															const s = step;
															return {
																title: s?.title,
																description: s?.description,
																status: s?.status,
																assigneeType: s?.assigneeType,
															};
														})
													: null,
												runbooks: Array.isArray(planWithExtras.runbooks)
													? planWithExtras.runbooks.map((runbook) => {
															const r = runbook;
															return {
																confidenceScore: r?.confidenceScore,
																title: r?.title,
																url: r?.url,
															};
														})
													: null,
												contentSources: Array.isArray(planWithExtras.contentSources)
													? planWithExtras.contentSources.map((source) => {
															const cs = source;
															return {
																ari: cs?.ari,
																confidenceScore: cs?.confidenceScore,
																title: cs?.title,
																url: cs?.url,
																type: cs?.type,
																product: cs?.product,
															};
														})
													: null,
											}
										: null,
								},
							};
						}
						return { resolved: true, timedOut: false, state: null };
					})
					.catch(() => ({ resolved: true, timedOut: true, state: null }));

				const [esaSettled, previewModeSettled, planSettled] = await raceToDeadline3(
					deadlinePromise,
					esaPromise,
					previewModePromise,
					planPromise,
				);

				// ── Build partial result ──
				const esaCheckResolved = esaSettled.status === 'fulfilled';
				const esaValidated = esaCheckResolved ? esaSettled.value.validated : false;

				// If ESA check resolved and user is NOT validated, short-circuit
				if (
					esaCheckResolved &&
					!esaValidated &&
					fg('show-resolution-panel-when-request-type-validated')
				) {
					return NOT_ELIGIBLE;
				}

				// Preview mode result (only meaningful when fg('preview_mode_enabled') is ON,
				// otherwise the promise resolves to { resolved: false, enabled: false }).
				const previewModeCheckResolved =
					previewModeSettled.status === 'fulfilled' && previewModeSettled.value.resolved === true;
				const previewModeEnabled = previewModeCheckResolved
					? previewModeSettled.value.enabled
					: false;

				const planResolved = planSettled.status === 'fulfilled';
				succeeded = true;

				return {
					eligible: true as const,
					projectId,
					issueId,
					projectAri,
					esaCheckResolved,
					esaValidated,
					previewModeCheckResolved,
					previewModeEnabled,
					planQueryResolved: planResolved,
					initialState: planResolved ? planSettled.value.state : null,
					planQueryTimedOut: planResolved ? planSettled.value.timedOut : true,
				};
			} finally {
				if (deadlineTimerId !== undefined) {
					clearTimeout(deadlineTimerId);
				}
				if (succeeded) {
					resolutionPlanSSRMetric.stop();
				} else {
					resolutionPlanSSRMetric.cancel();
				}
			}
		},
		maxAge: 30 * 1000, // 30s — plan data changes with user interactions
	});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getResolutionPlanSSRResource = (): RouteResource<any>[] => {
	return [resourceWithCondition(() => fg('resolution-plan-panel-ssr'), ResolutionPlanSSRResource)];
};
